<script setup lang="ts">
import { ref, onMounted } from 'vue'
import DraggableButton from './DraggableButton.vue'
import LoggerConfig from './LoggerConfig.vue'
import PWADebug from '@/components/ui/pwa/accountPage/PWADebug.vue'
import DevToolsManagerActions from './DevToolsManagerActions.vue'
import SignInWithToken from './SignInWithToken.vue'
import RemoteConfigDebug from './RemoteConfigDebug.vue'
import AnalyticsDebug from './AnalyticsDebug.vue'
import LiveClassDevScenarios from './LiveClassDevScenarios.vue'
import SentrySanitizationDebug from './SentrySanitizationDebug.vue'
import SwAuthDebug from './SwAuthDebug.vue'
import { installDevConsoleCapture } from '@/utils/devConsoleCapture'
const LOCAL_STORAGE_KEY = 'debugTools'
const isCustomTokenEnabled = import.meta.env.VITE_GH_FIREBASE_AUTH_CUSTOM_TOKEN_ENABLED === 'true'
const TABS = [
  {
    label: 'Actions',
    id: 'actions',
    component: DevToolsManagerActions,
  },
  {
    label: 'Logger',
    id: 'logger',
    component: LoggerConfig,
  },
  {
    label: 'PWA Debug',
    id: 'pwa',
    component: PWADebug,
  },
  {
    label: 'SW Auth',
    id: 'sw-auth',
    component: SwAuthDebug,
  },
  {
    label: 'Remote Config',
    id: 'remote-config',
    component: RemoteConfigDebug,
  },
  {
    label: 'Analytics',
    id: 'analytics',
    component: AnalyticsDebug,
  },
  {
    label: 'Live class',
    id: 'live-class',
    component: LiveClassDevScenarios,
  },
  {
    label: 'Sentry scrub',
    id: 'sentry-scrub',
    component: SentrySanitizationDebug,
  },
]

if (isCustomTokenEnabled) {
  TABS.push({
    label: 'Sign In with Token',
    id: 'signin-token',
    component: SignInWithToken,
  })
}

const isPopupOpen = ref(false)
const activeTab = ref(TABS[0].id)
const loggerConfigRef = ref()

onMounted(() => {
  checkIsEnabled()
  overrideFromQueryParam()
})

function togglePopup() {
  isPopupOpen.value = !isPopupOpen.value
  if (isPopupOpen.value) {
    installDevConsoleCapture()
  }
  if (isPopupOpen.value && activeTab.value === 'logger' && loggerConfigRef.value) {
    // Reload logger config when popup opens
    loggerConfigRef.value.loadConfig()
  }
}

function closePopup() {
  isPopupOpen.value = false
}
const isEnabled = ref(false)

const setInLocalStorage = (value: string) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, value)
}

const getFromLocalStorage = () => {
  return localStorage.getItem(LOCAL_STORAGE_KEY)
}

function checkIsEnabled() {
  if (!getFromLocalStorage()) {
    setInLocalStorage(
      import.meta.env.VITE_DEBUG_TOOLS_DEFAULT_VISIBLE === 'true' ? 'true' : 'false',
    )
  }

  const localStorageValue = getFromLocalStorage()

  isEnabled.value = localStorageValue === 'true'
}

const overrideFromQueryParam = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const override = urlParams.get('debugtools')
  if (override) {
    const value = override === 'true' ? 'true' : 'false'
    setInLocalStorage(value)
    isEnabled.value = value === 'true'
  }
}
</script>

<template>
  <div class="logger-manager">
    <!-- Floating Button -->
    <DraggableButton
      @click="togglePopup"
      :is-active="isPopupOpen"
      :is-visible="isEnabled"
      title="Logger Settings (Drag to move)"
      storage-key="logger_button_position"
    >
      <button class="logger-button">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          ></path>
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          ></path>
        </svg>
      </button>
    </DraggableButton>

    <!-- Popup Modal -->
    <div v-if="isPopupOpen" class="logger-overlay" @click="closePopup">
      <div class="logger-popup" @click.stop>
        <div class="logger-header">
          <h3 class="logger-title">Dev Settings</h3>
          <button @click="closePopup" class="logger-close">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <div class="logger-content">
          <!-- Tabs -->
          <div class="dev-tabs">
            <button
              v-for="tab in TABS"
              :key="tab.label"
              :class="['dev-tab', { active: activeTab === tab.id }]"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>

          <div class="dev-tab-content min-h-[70vh]">
            <component
              :is="tab.component"
              v-for="tab in TABS.filter((tab) => tab.id === activeTab)"
              :key="tab.label"
              ref="tabRefs"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.logger-manager {
  position: relative;
  z-index: 1000;
}

.logger-button {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: var(--color-development-color);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.logger-button:hover {
  background: var(--color-development-color);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.logger-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.logger-popup {
  background: white;
  border-radius: 12px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.logger-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.logger-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.logger-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s ease;
}

.logger-close:hover {
  color: #374151;
}

.logger-content {
  padding: 24px;
  overflow-y: auto;
}

.logger-section {
  margin-bottom: 24px;
  display: flex;
  flex-direction: row;
  gap: 12px;
}

.dev-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.dev-tab {
  padding: 12px 16px;
  cursor: pointer;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  position: relative;
  transition: color 0.2s ease;
}

.dev-tab:hover {
  color: #111827;
}

.dev-tab.active {
  color: #3b82f6;
  font-weight: 600;
}

.dev-tab.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #3b82f6;
}

@media (max-width: 640px) {
  .logger-popup {
    width: 95%;
    margin: 20px;
  }

  .logger-content {
    padding: 16px;
  }

  .logger-header {
    padding: 16px 20px;
  }
}
</style>
